// src/hooks/useMasseuses.js

import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import shuffle from '../utils/shuffle';
import { add, format } from 'date-fns';
import { API_URI } from '../constants/appConstants';
import techniqueChecker from '../utils/techniqueChecker';

const useMasseuses = (values) => {
  const fetchMasseuses = async () => {
    const {
      page = 1,
      page_size = 1000,
      bust_enhanced = '',
      services = '',
      newMasseuse: new_masseuse = '',
      language = '',
      tattoos = '',
      available_on,
      hair_color = '',
      background = '',
      technique,
      active = 'true',
      profile_visible = 'true',
    } = values;

    try {
      // Create params object with default values using destructuring
      const params = {
        active,
        page,
        page_size,
        bust_enhanced,
        services,
        new: new_masseuse,
        language,
        tattoos,
        hair_color,
        background,
        profile_visible,
      };

      // Use Promise.all to make parallel API requests
      const [masseusesResponse, scheduleResponse] = await Promise.all([
        axios.get(`${API_URI}/masseuses/`, { params }),
        axios.get(`${API_URI}/schedule/`),
      ]);

      let data = masseusesResponse.data;
      const schedule = scheduleResponse.data;

      // Request available masseuses only if needed
      let availableMasseuses = [];
      if (available_on === 'today') {
        const availableResponse = await axios.get(
          `${API_URI}/masseuses/available/`
        );
        availableMasseuses = availableResponse.data;
      }

      // Calculate tomorrow's available masseuses if needed
      let tomorrowAvailableMasseuses = [];
      if (available_on === 'tomorrow') {
        const tomorrow = add(new Date(), { days: 1 });
        const tomorrowDate = format(tomorrow, 'yyyy-MM-dd');
        tomorrowAvailableMasseuses = schedule.filter((item) =>
          item.start.startsWith(tomorrowDate)
        );
      }

      // Sort and prioritize masseuses based on technique and vacation status
      if (techniqueChecker(technique)) {
        const nonVacationSensualMasseuses = data.results.filter(
          (masseuse) =>
            techniqueChecker(masseuse.technique) && !masseuse.vacation
        );
        const vacationSensualMasseuses = data.results.filter(
          (masseuse) =>
            techniqueChecker(masseuse.technique) && masseuse.vacation
        );

        data.results = [
          ...shuffle(nonVacationSensualMasseuses),
          ...vacationSensualMasseuses,
        ];
      } else if (technique === 'Shiatsu') {
        const nonVacationShiatsuMasseuses = data.results.filter(
          (masseuse) => masseuse.technique === 'Shiatsu' && !masseuse.vacation
        );
        const vacationShiatsuMasseuses = data.results.filter(
          (masseuse) => masseuse.technique === 'Shiatsu' && masseuse.vacation
        );

        data.results = [
          ...shuffle(nonVacationShiatsuMasseuses),
          ...vacationShiatsuMasseuses,
        ];
      } else {
        const sensualMasseuses = data.results.filter(
          (masseuse) =>
            techniqueChecker(masseuse.technique) && !masseuse.vacation
        );
        const vacationMasseuses = data.results.filter(
          (masseuse) => masseuse.vacation
        );
        const shiatsuMasseuses = data.results.filter(
          (masseuse) => masseuse.technique === 'Shiatsu' && !masseuse.vacation
        );

        data.results = [
          ...shuffle(sensualMasseuses),
          ...vacationMasseuses,
          ...shuffle(shiatsuMasseuses),
        ];
      }

      const todayAvlMasseuses = [];
      const tomorrowAvlMasseuses = [];

      // Create Set for faster lookups
      const availableMasseusesSet = new Set(
        availableMasseuses.map((m) => m.id)
      );

      // Process each masseuse
      data.results.forEach((masseuse) => {
        // Filter schedule once per masseuse
        masseuse.schedule = schedule.filter(
          (s) =>
            s.name === masseuse.name && s.background === masseuse.background
        );

        // Check availability using Set
        if (
          available_on === 'today' &&
          availableMasseusesSet.has(masseuse.id)
        ) {
          todayAvlMasseuses.push(masseuse);
        }

        if (available_on === 'tomorrow') {
          const isTomorrowAvailable = tomorrowAvailableMasseuses.some(
            (m) =>
              m.name === masseuse.name && m.background === masseuse.background
          );

          if (isTomorrowAvailable) {
            tomorrowAvlMasseuses.push(masseuse);
          }
        }

        // Main Images - optimize the image handling
        if (masseuse.images && masseuse.image) {
          const index = masseuse.images.indexOf(masseuse.image);
          if (index !== -1) {
            masseuse.images.splice(index, 1);
          }
        }

        // Create thumbnails in a single pass
        if (masseuse.images) {
          masseuse.newThumbnails = masseuse.images.map(
            (img) =>
              'https://clubdynasty.ca/media/users/thumbnails' +
              img.split('images')[1]
          );
        } else {
          masseuse.newThumbnails = [];
        }

        // Optimize thumbnails handling
        if (
          masseuse.thumbnails &&
          masseuse.thumbnails.length > 0 &&
          masseuse.image
        ) {
          const imagePrefix = masseuse.image.split('images')[0];
          for (let i = 0; i < masseuse.thumbnails.length; i++) {
            if (masseuse.thumbnails[i].startsWith(imagePrefix)) {
              masseuse.thumbnails.splice(i, 1);
              break;
            }
          }
        }
      });

      // Filter results based on availability
      if (available_on === 'today') {
        return todayAvlMasseuses;
      } else if (available_on === 'tomorrow') {
        return tomorrowAvlMasseuses;
      }

      return data.results;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch masseuses');
    }
  };

  // Convert to TanStack React Query v5 syntax
  return useQuery({
    queryKey: ['masseuses', values],
    queryFn: fetchMasseuses,
    gcTime: Infinity, // renamed from cacheTime in v5
    staleTime: Infinity,
  });
};

export default useMasseuses;
